import React, { useState, useEffect } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import Add from "@material-ui/icons/Add";
import Remove from "@material-ui/icons/Remove";

/**
 * Componente para renderizar un item individual del carrito
 * @param {Object} producto - Objeto del producto
 * @param {boolean} isSelected - Si el producto está seleccionado
 * @param {Function} onCheckboxChange - Callback cuando cambia el checkbox
 * @param {Function} onQuantityChange - Callback cuando cambia la cantidad (recibe incrementar: boolean o cantidad: number)
 * @param {Function} onDelete - Callback para eliminar el producto
 * @param {Function} formatPrice - Función para formatear el precio
 */
const CartItem = ({
	producto,
	isSelected,
	onCheckboxChange,
	onQuantityChange,
	onDelete,
	formatPrice,
	porcentajeDescuento = null,
	montoDescuento = null,
}) => {
	const [quantityInput, setQuantityInput] = useState(String(producto.Cantidad || 1));
	const [isEditing, setIsEditing] = useState(false);

	// Sincronizar el input cuando cambia la cantidad del producto (desde botones +-)
	useEffect(() => {
		if (!isEditing) {
			setQuantityInput(String(producto.Cantidad || 1));
		}
	}, [producto.Cantidad, isEditing]);

	const precioUnitario = producto.product?.public_price || 0;
	const cantidad = producto.Cantidad || 1;
	// Calcular el total siempre basado en precio unitario * cantidad actual
	const precioTotal = precioUnitario * cantidad;

	const handleQuantityInputChange = (e) => {
		const value = e.target.value;
		// Permitir solo números y campo vacío temporalmente
		if (value === "" || /^\d+$/.test(value)) {
			setQuantityInput(value);
		}
	};

	const handleQuantityInputBlur = () => {
		setIsEditing(false);
		const cantidad = parseInt(quantityInput, 10);
		
		// Validar que sea un número válido y mayor a 0
		if (isNaN(cantidad) || cantidad < 1 || quantityInput === "") {
			// Restaurar valor anterior si es inválido
			setQuantityInput(String(producto.Cantidad || 1));
			return;
		}

		// Si la cantidad cambió, actualizar
		if (cantidad !== (producto.Cantidad || 1)) {
			onQuantityChange(cantidad);
		} else {
			// Asegurar que el input muestre el valor correcto incluso si no cambió
			setQuantityInput(String(cantidad));
		}
	};

	const handleQuantityInputKeyPress = (e) => {
		if (e.key === "Enter") {
			e.target.blur();
		}
	};

	return (
		<div className="carrito-producto-item">
			{/* Información del producto */}
			<div className="carrito-producto-info">
				<Checkbox
					checked={isSelected}
					onChange={onCheckboxChange}
					color="primary"
					className="carrito-checkbox"
				/>
				<div className="carrito-producto-imagen">
					{producto.image ? (
						<img
							src={producto.image}
							alt={`${producto.product?.description || ""} / ${producto.desing
								?.name || ""}`}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
							}}
						/>
					) : (
						<span>Imagen</span>
					)}
				</div>
				<div className="carrito-producto-details">
					<h3>{`${producto.product?.description || ""} / ${producto.desing
						?.name || ""}`}</h3>
					<div className="detail-item">
						Talla: {producto.product?.size || ""}
					</div>
					<button onClick={onDelete} className="eliminar-button" type="button">
						Eliminar
					</button>
				</div>
			</div>
			{/* Precio y controles */}
			<div className="carrito-producto-precio">
				<div className="precio-info">
					<div className="precio-con-badge">
						<div className={`precio-total ${porcentajeDescuento && porcentajeDescuento > 0 ? 'precio-tachado' : ''}`}>
							{formatPrice(precioTotal)}
						</div>
						{porcentajeDescuento && porcentajeDescuento > 0 && (
							<div className="porcentaje-descuento-badge">
								-{porcentajeDescuento}%
							</div>
						)}
					</div>
					{porcentajeDescuento && porcentajeDescuento > 0 && (
						<div className="precio-con-descuento">
							{formatPrice(precioTotal - (montoDescuento || 0))}
						</div>
					)}
				</div>
				{/* Controles de cantidad */}
				<div className="carrito-cantidad-controls">
					<button
						className="cantidad-button"
						onClick={() => onQuantityChange(false)}
						disabled={producto.Cantidad <= 1}
						type="button"
					>
						<Remove />
					</button>
					<input
						type="text"
						className="cantidad-input"
						value={quantityInput}
						onChange={handleQuantityInputChange}
						onBlur={handleQuantityInputBlur}
						onKeyPress={handleQuantityInputKeyPress}
						onFocus={() => setIsEditing(true)}
						min="1"
						style={{
							width: "50px",
							textAlign: "center",
							border: "1px solid #ddd",
							borderRadius: "4px",
							padding: "4px 8px",
							fontSize: "16px",
							outline: "none",
						}}
					/>
					<button
						className="cantidad-button"
						onClick={() => onQuantityChange(true)}
						type="button"
					>
						<Add />
					</button>
				</div>
			</div>
		</div>
	);
};

export default CartItem;
